import { Box, Stack } from '@mui/material';
import { isEmpty } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useFieldArray, useWatch } from 'react-hook-form';

import { Button } from '@/components/form/baseInputs/Button';
import { useFormFieldsDisabled } from '@/components/form/context/formFieldsDisabled.context';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { SortableList } from '@/components/lists/DragAndDropList/SortableList';
import { useFormContext } from '@/components/react-hook-form';
import { DispositiveProvisionTemplateKind } from '@/types/schema';

import { useDispositiveProvisionsContext } from '../../contexts/dispositiveProvisions.context';
import {
  CombinedPaths,
  DispositionScheme,
} from '../../dispositiveProvisions.types';
import { DispositiveProvisionsTemplateEditorTabHeader } from '../../DispositiveProvisionsTemplateSplitScreenModal/DispositiveProvisionsTemplateSplitScreenModal.components';
import {
  defaultRecipient,
  DISPOSITIVE_PROVISIONS_FORM_NAMESPACE,
} from '../DispositiveProvisionsForm.constants';
import {
  DispositiveProvisionsFormPaths,
  DispositiveProvisionsFormShape,
} from '../DispositiveProvisionsForm.types';
import {
  useDispositionScheme,
  useIsImmutableDeathOrder,
} from '../hooks/utilityHooks';
import { DispositiveProvisionsRecipientsEmptyState } from './DispositiveProvisionsRecipientsEmptyState';
import { DraggableListItemRecipient } from './DraggableListItemRecipient';
import { InitialDispositionSchemeSelector } from './InitialDispositionSchemeSelector';
import { SortableRecipientItem } from './SortableRecipientItem';

interface DispositiveProvisionsFormRecipientsProps {
  onOpenSchemeSelectionModal: (() => void) | null;
  /**
   * If true, always expand entries by default (eg: on load)
   */
  defaultExpandAll?: boolean;
  templateType?: DispositiveProvisionTemplateKind;
}

export function DispositiveProvisionsRecipients({
  onOpenSchemeSelectionModal,
  defaultExpandAll = false,
  templateType,
}: DispositiveProvisionsFormRecipientsProps) {
  const isImmutableDeathOrder = useIsImmutableDeathOrder();
  const { isTwoClientHousehold } = useDispositiveProvisionsContext();
  const [forceExpandId, setForceExpandId] = useState<string | null>(null);
  const {
    control,
    setValue,
    getValues,
    setShouldBlockNavigation,
    resetField,
    setFocus,
  } = useFormContext<DispositiveProvisionsFormShape>();
  const { disabled: disabledFromContext } = useFormFieldsDisabled();

  // we use this to know when to force expand the most recently added recipient;
  // there's probably a cleaner way to do this but i can't think of it right now
  const hasUnhandledAddition = useRef(false);

  // this is to make sure we don't add multiple empty recipients when we auto-select
  // the disposition scheme when we have AI DP enabled and recipient suggestions
  // for a single-client household.
  const hasAutoSelectedScheme = useRef(false);

  const {
    append,
    remove,
    move,
    fields: recipients,
  } = useFieldArray({
    control,
    name: `${DISPOSITIVE_PROVISIONS_FORM_NAMESPACE}.recipients` as const satisfies DispositiveProvisionsFormPaths,
    keyName: '_id',
  });

  useEffect(() => {
    if (!hasUnhandledAddition.current) return;
    const mostRecentlyAddedRecipient = recipients[recipients.length - 1];
    if (mostRecentlyAddedRecipient) {
      setForceExpandId(mostRecentlyAddedRecipient._id);

      // Focus the new recipient input so the user can pick a recipient without
      // clicking into it. The typeahead's menu stays closed on focus
      // (openOnFocus is disabled on the recipient field). Deferred so the
      // accordion has expanded and the input is mounted.
      const recipientIdPath =
        `${DISPOSITIVE_PROVISIONS_FORM_NAMESPACE}.recipients.${recipients.length - 1}.recipientId` as DispositiveProvisionsFormPaths;
      setTimeout(() => setFocus(recipientIdPath));
    }
    hasUnhandledAddition.current = false;
  }, [recipients, recipients.length, setFocus]);

  const handleAddRecipient = useCallback(() => {
    append(defaultRecipient, { shouldFocus: false });
    hasUnhandledAddition.current = true;
  }, [append]);

  const handleRemoveRecipient = useCallback(
    (index: number) => {
      const basePath =
        `${DISPOSITIVE_PROVISIONS_FORM_NAMESPACE}.recipients.${index}` as const;

      resetField(
        `${basePath}.transferTaxKind` as const satisfies CombinedPaths
      );

      remove(index);
    },
    [remove, resetField]
  );

  const handleReorder = useCallback(
    (oldIndex: number, newIndex: number) => {
      move(oldIndex, newIndex);

      // move() shifts field values between indices but doesn't preserve
      // per-field dirty state. This causes useDefaultRecipientTransferTaxKind
      // to see isDirty=false and overwrite user-set transferTaxKind values.
      // Re-setting the values with shouldDirty marks them as user-modified.
      const minIdx = Math.min(oldIndex, newIndex);
      const maxIdx = Math.max(oldIndex, newIndex);
      for (let i = minIdx; i <= maxIdx; i++) {
        const fieldPath =
          `${DISPOSITIVE_PROVISIONS_FORM_NAMESPACE}.recipients.${i}.transferTaxKind` as DispositiveProvisionsFormPaths;
        const currentValue = getValues(fieldPath);
        if (currentValue) {
          setValue(fieldPath, currentValue as never, { shouldDirty: true });
        }
      }
    },
    [move, getValues, setValue]
  );

  const hasBeenReviewed = useWatch({
    control,
    name: `${DISPOSITIVE_PROVISIONS_FORM_NAMESPACE}._hasBeenReviewed` as const satisfies DispositiveProvisionsFormPaths,
  });

  const dispositionScheme = useDispositionScheme();

  function setDispositionSchema(selection: DispositionScheme) {
    setValue('dispositiveProvisions.dispositionScheme', selection);
    setValue('dispositiveProvisions._hasBeenReviewed', true);
    // This should block navigation, because we want the form to submit when the user navigates to
    // another subform.
    setShouldBlockNavigation(true);
  }

  function handleInitialSchemeSelection(selection: DispositionScheme) {
    setDispositionSchema(selection);
    if (
      selection !== DispositionScheme.NONE &&
      !hasAutoSelectedScheme.current
    ) {
      handleAddRecipient();
      hasAutoSelectedScheme.current = true;
    }
  }

  function handleAddEmptyStateRecipient() {
    if (isImmutableDeathOrder) {
      // make sure we're not stuck in DispositionScheme.NONE; we clearly want to no longer be in that mode
      // if we're adding a recipient
      setDispositionSchema(dispositionScheme);
      return handleAddRecipient();
    }

    // with a two-client household, we need the user to choose the disposition order before adding recipients
    if (isTwoClientHousehold) {
      onOpenSchemeSelectionModal?.();
    } else {
      // with a single-client household, we can just set the disposition scheme directly because it's the only option
      setDispositionSchema(DispositionScheme.UPON_FIRST_DEATH);
    }

    handleAddRecipient();
  }

  // if this is the user's first time setting up dispositive provisions for this client/entity/testamentary entity, we want to
  // give them a more robust and descriptive empty state
  if (dispositionScheme === DispositionScheme.NONE && !hasBeenReviewed) {
    return (
      <InitialDispositionSchemeSelector
        onSchemeSelection={handleInitialSchemeSelection}
      />
    );
  }

  if (isEmpty(recipients) || dispositionScheme === DispositionScheme.NONE) {
    return (
      <Box pt={2}>
        <DispositiveProvisionsRecipientsEmptyState
          onAddRecipients={handleAddEmptyStateRecipient}
        />
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      {templateType && (
        <DispositiveProvisionsTemplateEditorTabHeader
          templateType={templateType}
          recipients={recipients}
          onAddTemplateRecipient={append}
        />
      )}
      <SortableList
        items={recipients}
        getId={(r) => r._id}
        onReorder={handleReorder}
        disabled={disabledFromContext}
      >
        <Box component="ul" sx={{ p: 0, m: 0, listStyleType: 'none' }}>
          {recipients.map((r, i) => {
            return (
              <SortableRecipientItem
                key={r._id}
                id={r._id}
                disabled={disabledFromContext}
              >
                {(Draghandle) => (
                  <DraggableListItemRecipient
                    onRemove={() => handleRemoveRecipient(i)}
                    defaultExpanded={
                      forceExpandId === r._id || defaultExpandAll
                    }
                    index={i}
                    Draghandle={Draghandle}
                    templateType={templateType}
                  />
                )}
              </SortableRecipientItem>
            );
          })}
        </Box>
      </SortableList>

      {!templateType && (
        <Button
          size="sm"
          fullWidth
          variant="secondary"
          disabled={disabledFromContext}
          startIcon={PlusIcon}
          onClick={() => handleAddRecipient()}
        >
          Add additional recipient
        </Button>
      )}
    </Stack>
  );
}
